/**
 * EditDescriptionsModal (S1.4) — chunked edit for the three doc descriptions
 * (sales 類別A / purchase 類別T / customs 報關品名) with their doc-endpoint
 * captions. All three gate 產品就緒 but never block the save (two-tier).
 *
 * Y-S3: also edits 採購單備註 purchaseOrderNote (DD!46 Should) — flows onto
 * the vendor PO doc like purchaseDescription; never gates 產品就緒.
 */

import { useEffect, useMemo } from 'react';
import { Input, Modal, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { useProductMutations } from '@/hooks/useProducts';
import type { Product } from '@/types/api/product';
import { editDescriptionsSchema } from '../schemas';
import type { EditDescriptionsFormData } from '../schemas';

const { Text } = Typography;

interface EditDescriptionsModalProps {
  open: boolean;
  product: Product;
  onCancel: () => void;
  onSaved: () => void;
}

export const EditDescriptionsModal = ({
  open,
  product,
  onCancel,
  onSaved,
}: EditDescriptionsModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { handleError, handleSuccess } = useErrorHandler();
  const { updateProduct } = useProductMutations(product.id);

  const formValues = useMemo<EditDescriptionsFormData>(
    () => ({
      salesDescription: product.salesDescription ?? '',
      purchaseDescription: product.purchaseDescription ?? '',
      customsDescription: product.customsDescription ?? '',
      purchaseOrderNote: product.purchaseOrderNote ?? '', // DD!46 (Y-S3)
    }),
    [product]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<EditDescriptionsFormData>({
    resolver: zodResolver(editDescriptionsSchema),
    defaultValues: formValues,
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(formValues);
  }, [open, formValues, reset]);

  const onSubmit = async (data: EditDescriptionsFormData) => {
    try {
      await updateProduct({
        salesDescription: data.salesDescription ?? '',
        purchaseDescription: data.purchaseDescription ?? '',
        customsDescription: data.customsDescription ?? '',
        purchaseOrderNote: data.purchaseOrderNote ?? '', // DD!46 (Y-S3)
      });
      handleSuccess(t('products:messages.updated'));
      onSaved();
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  const descriptionField = (
    name: keyof EditDescriptionsFormData,
    labelKey: string,
    captionKey: string
  ) => (
    <div style={formStyles.fieldContainerStyle}>
      <Text strong style={formStyles.sectionTitleStyle}>
        {t(labelKey)}
      </Text>
      <Text
        type="secondary"
        style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: '8px' }}
      >
        {t(captionKey)}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Input.TextArea
            {...field}
            rows={3}
            style={{ ...formStyles.inputStyle, height: 'auto' }}
          />
        )}
      />
    </div>
  );

  return (
    <Modal
      title={t('products:detail.modals.editDescriptions')}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit(onSubmit)}
      okText={t('common:actions.save')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={isSubmitting}
      width={800}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
    >
      <form>
        {descriptionField(
          'salesDescription',
          'products:fields.salesDescription',
          'products:wizard.descriptionsHelp.sales'
        )}
        {descriptionField(
          'purchaseDescription',
          'products:fields.purchaseDescription',
          'products:wizard.descriptionsHelp.purchase'
        )}
        {descriptionField(
          'customsDescription',
          'products:fields.customsDescription',
          'products:wizard.descriptionsHelp.customs'
        )}
        {descriptionField(
          'purchaseOrderNote',
          'products:fields.purchaseOrderNote',
          'products:wizard.descriptionsHelp.purchaseOrderNote'
        )}
      </form>
    </Modal>
  );
};

export default EditDescriptionsModal;
